import { useQuery, useMutation, keepPreviousData } from '@tanstack/react-query';
import { queryClient } from '@/shared/services/queryClient';
import { apiClient } from '@/shared/services/api';
import {
  customersApi,
} from '../services/customersApi';
import type {  CreateCustomerRequest,
  UpdateCustomerRequest, CustomerListQuery} from '../services/customersApi';

interface ApiResponse<T> {
  success: boolean;
  message: string;
  data: T;
}

export const useCustomers = (query: CustomerListQuery = {}) => {
  return useQuery({
    queryKey: ['customers', query],
    queryFn: () => customersApi.getCustomers(query),
    placeholderData: keepPreviousData,
  });
};

export const useCustomerById = (id: string) => {
  return useQuery({
    queryKey: ['customers', id],
    queryFn: () => customersApi.getCustomerById(id),
  });
};

export const useSearchCustomers = (searchTerm: string) => {
  const term = searchTerm.trim();
  return useQuery({
    queryKey: ["customers", "search", term],
    queryFn: async ({ signal }): Promise<CustomerSearchResult[]> => {
      const { data } = await apiClient.get<ApiResponse<CustomerSearchResult[]>>(
        "/Customers/search",
        {
          params: {
            searchTerm: term,
            limit: 20,
          },
          signal,
        }
      );
      return data.data;
    },
    enabled: term.length >= 2,
    staleTime: 30000, // 30 seconds
  });
};

export interface CustomerSearchResult {
  id: string;
  customerName: string;
  mobileNo1: string;
}

export const useCreateCustomer = () => {
  return useMutation({
    mutationFn: (payload: CreateCustomerRequest) =>
      customersApi.createCustomer(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customers'] });
    },
  });
};

export const useUpdateCustomer = (id: string) => {
  return useMutation({
    mutationFn: (payload: UpdateCustomerRequest) =>
      customersApi.updateCustomer(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      queryClient.invalidateQueries({ queryKey: ['customers', id] });
    },
  });
};

export const useDeleteCustomer = () => {
  return useMutation({
    mutationFn: (id: string) => customersApi.deleteCustomer(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customers'] });
    },
  });
};

export const useImportCustomers = () => {
  return useMutation({
    mutationFn: (file: File) => customersApi.importCustomers(file),
    // Don't invalidate here — the background job will push a SignalR event when done
  });
};

export const useImportJobStatus = (jobId: string | null) => {
  return useQuery({
    queryKey: ['importJob', jobId],
    queryFn: () => customersApi.getImportJobStatus(jobId!),
    enabled: !!jobId,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      // Stop polling once terminal
      if (status === 'Completed' || status === 'Failed') return false;
      return 3000; // Poll every 3s while Pending/Processing
    },
  });
};

export const useDownloadImportTemplate = () => {
  return useMutation({
    mutationFn: () => customersApi.downloadImportTemplate(),
  });
};

export const useSetSowingPaymentThreshold = (customerId: string) => {
  return useMutation({
    mutationFn: (thresholdPercentage: number | null) =>
      customersApi.setSowingPaymentThreshold(customerId, thresholdPercentage),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      queryClient.invalidateQueries({ queryKey: ['customers', customerId] });
    },
  });
};
